import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import DOMPurify from 'dompurify';
import { useData } from '../context/DataContext';
import Marquee from '../components/Marquee';
import Events from '../components/Events';
import styles from './Home.module.css';

import heroVideo from '../assets/WhatsApp Video 2026-03-29 at 14.54.58.mp4';

// Default brand images (fallback)
import brand1 from '../assets/brand1.jpg';
import brand2 from '../assets/brand2.jpg';
import brand3 from '../assets/brand3.jpg';
import brand4 from '../assets/brand4.jpg';
import brand5 from '../assets/brand5.jpg';

const defaultBrands = [brand1, brand2, brand3, brand4, brand5];

// Sanitizer
const clean = (val) => DOMPurify.sanitize(val, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

export default function Home() {
  const { artists, gallery, settings } = useData();
  const fadeRefs = useRef([]);
  const formRef = useRef();
  const [formStatus, setFormStatus] = useState('idle'); // idle | sending | sent | error
  const lastSubmitRef = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add(styles.visible);
      }),
      { threshold: 0.1 }
    );
    fadeRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addFadeRef = (el) => {
    if (el && !fadeRefs.current.includes(el)) fadeRefs.current.push(el);
  };

  // Brand images: use uploaded or default
  const bi = (idx) => (settings.brandImages && settings.brandImages[idx]) || defaultBrands[idx];

  // About heading split for styling
  const aboutParts = (settings.aboutHeading || 'HOUSE OF LINE.').split(' ');
  const lastWord = aboutParts.pop();
  const firstWords = aboutParts.join(' ');

  return (
    <main>
      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.xDeco}>X</div>
          <p className={styles.heroTag}>{settings.heroTag || '— Eskişehir / Est. 2024'}</p>
          <h1 className={styles.heroTitle}>
            <span className={styles.titleX}>X</span>
            <br />
            <span className={styles.titleDistress}>HAUS</span>
          </h1>
          <p className={styles.heroSubtitle}>{settings.heroSubtitle || 'Tatto · Art · Studio'}</p>
          <div className={styles.heroCta}>
            <a href="#contact" className="btn-primary">{settings.heroCtaText || 'Randevu Al'}</a>
            <a href="#gallery" className={styles.ctaSecondary}>{settings.heroCtaSecondary || 'Galeriyi Gör'}</a>
          </div>
        </div>
        <div className={styles.heroRight}>
          {settings.heroMediaType === 'image'
            ? <img src={settings.heroImage || heroVideo} alt="X HAUS" />
            : <video src={settings.heroVideo || heroVideo} autoPlay muted loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          }
        </div>
        <div className={styles.heroScroll}>SCROLL</div>
      </section>

      {/* ── MARQUEE ── */}
      <Marquee />

      {/* ── BRAND IMAGE GRID ── */}
      <section className={styles.brandGrid}>
        <div className={styles.gridTop}>
          <div className={styles.brandImg}><img src={bi(2)} alt="X HAUS" /></div>
          <div className={styles.brandImg}><img src={bi(3)} alt="X HAUS" /></div>
        </div>
        <div className={styles.gridBottom}>
          <div className={styles.brandImg}><img src={bi(1)} alt="X HAUS" /></div>
          <div className={styles.brandImg}><img src={bi(4)} alt="X HAUS" /></div>
          <div className={styles.brandRedCell}>
            <div className={styles.brandRedTitle}>{(settings.brandRedTitle || 'X\nHAUS').split('\n').map((l, i) => <React.Fragment key={i}>{i > 0 && <br />}{l}</React.Fragment>)}</div>
            <div className={styles.brandRedSub}>{settings.brandRedSub || 'Art You Carry'}</div>
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section className={styles.about} id="about">
        <div className={styles.aboutLeft} ref={addFadeRef}>
          <div className="section-label">Hakkımızda</div>
          <h2 className={styles.aboutHeading}>
            {firstWords}<br />{lastWord && <>{lastWord.split('.')[0]} <span>{lastWord.includes('.') ? '.' : ''}</span></>}
          </h2>
          <div className={styles.stats}>
            {(settings.stats || []).map((s, i) => (
              <div key={i} className={styles.stat}>
                <div className={styles.statNum}>{s.num}</div>
                <div className={styles.statLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.aboutRight} ref={addFadeRef}>
          <p className={styles.aboutText}>{settings.aboutText}</p>
          <p className={styles.aboutQuote}>"{settings.aboutQuote || 'Art You Carry.'}"</p>
          <a href="#contact" className="btn-primary" style={{ marginTop: 40 }}>{settings.heroCtaText || 'Randevu Al'}</a>
        </div>
      </section>

      {/* ── ARTISTS ── */}
      <section className={styles.artists} id="artists">
        <div className="section-label">{settings.artistsSectionLabel || 'Artistlerimiz'}</div>
        <h2 className={styles.artistsHeading}>{settings.artistsSectionHeading || 'EKİBİMİZ'}</h2>
        <div className={styles.artistsGrid}>
          {artists.map((artist, i) => (
            <Link
              to={`/artist/${artist.id}`}
              key={artist.id}
              className={`${styles.artistCard} ${styles.fadeUp}`}
              ref={addFadeRef}
              style={{ transitionDelay: `${i * 0.1}s`, textDecoration: 'none', color: 'inherit' }}
            >
              <div className={styles.artistImg}>
                {artist.photo
                  ? <img src={artist.photo} alt={artist.name} />
                  : <div className={styles.artistPlaceholder}>{artist.name[0]}</div>
                }
              </div>
              <div className={styles.artistInfo}>
                <div className={styles.artistName}>{artist.name}</div>
                <div className={styles.artistStyle}>{artist.style}</div>
                <div className={styles.artistTags}>
                  {artist.tags.map(t => (
                    <span key={t} className={styles.artistTag}>{t}</span>
                  ))}
                </div>
                <span className={`${styles.availBadge} ${artist.available ? styles.open : styles.closed}`}>
                  {artist.available ? 'Randevu Açık' : 'Randevu Dolu'}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── GALLERY ── */}
      <section className={styles.gallery} id="gallery">
        <div className="section-label">{settings.gallerySectionLabel || 'Galeri'}</div>
        <h2 className={styles.galleryHeading}>{settings.gallerySectionHeading || 'ÇALIŞMALARIMIZ'}</h2>
        <div className={styles.galleryGrid}>
          {gallery.map((item) => (
            <div key={item.id} className={styles.galleryItem}>
              {item.url
                ? <img src={item.url} alt={item.title} />
                : <div className={styles.galleryPlaceholder}>{item.title}</div>
              }
              <div className={styles.galleryOverlay}>
                <div className={styles.galleryTitle}>{item.title}</div>
                <div className={styles.galleryArtist}>{item.artist}</div>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.galleryCta}>
          <a href="#" className="btn-outline">Tüm Çalışmaları Gör</a>
        </div>
      </section>

      {/* ── EVENTS ── */}
      <Events />

      {/* ── CONTACT ── */}
      <section className={styles.contact} id="contact">
        <div className={styles.contactGrid}>
          <div>
            <div className="section-label">{settings.contactSectionLabel || 'İletişim'}</div>
            <h2 className={styles.contactHeading}>
              {(settings.contactSectionHeading || 'RANDEVU AL.').split(' ').map((w, i, arr) => (
                <React.Fragment key={i}>
                  {i === arr.length - 1 ? <span>{w}</span> : w}
                  {i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </h2>
            <div className={styles.contactDetails}>
              <div className={styles.detailBlock}>
                <strong>Adres</strong>
                <p>{settings.address}</p>
              </div>
              <div className={styles.detailBlock}>
                <strong>Instagram</strong>
                <p>{settings.instagram}</p>
              </div>
              <div className={styles.detailBlock}>
                <strong>E-posta</strong>
                <p>{settings.email}</p>
              </div>
              <div className={styles.detailBlock}>
                <strong>Çalışma Saatleri</strong>
                <p>{settings.hours}<br />Pazar: Kapalı</p>
              </div>
            </div>
          </div>
          <div>
            <div className={styles.mapContainer}>
              <iframe
                src={settings.mapEmbedUrl}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="X HAUS Konum"
              />
              <div className={styles.mapPin}>
                <div className={styles.mapPinLabel}>{settings.mapPinLabel || 'Konumumuz'}</div>
                <div className={styles.mapPinAddr}>{settings.mapPinAddr || 'Tepebaşı / ESKİŞEHİR'}</div>
              </div>
            </div>
            <a
              href={settings.mapLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.mapOpenLink}
            >
              ↗ Google Maps'te Aç
            </a>
            <form className={styles.contactForm} ref={formRef} onSubmit={async (e) => {
              e.preventDefault();
              // Honeypot check
              const hp = e.target.elements._hp;
              if (hp && hp.value) return;
              // Rate limit: 30 sn arası
              const now = Date.now();
              if (now - lastSubmitRef.current < 30000) {
                setFormStatus('error');
                return;
              }
              lastSubmitRef.current = now;

              const name = clean(e.target.elements.name.value);
              const email = clean(e.target.elements.email.value);
              const phone = clean(e.target.elements.phone.value);
              const message = clean(e.target.elements.message.value);

              // EmailJS varsa gönder
              if (settings.emailServiceId && settings.emailPublicKey && settings.emailTemplateContact) {
                setFormStatus('sending');
                try {
                  await emailjs.send(
                    settings.emailServiceId,
                    settings.emailTemplateContact,
                    {
                      from_name: name,
                      from_email: email,
                      phone: phone,
                      message: message,
                      to_email: settings.notificationEmail || settings.email,
                    },
                    settings.emailPublicKey
                  );
                  setFormStatus('sent');
                  formRef.current.reset();
                } catch (err) {
                  console.error('Email error:', err);
                  setFormStatus('error');
                }
              } else {
                // EmailJS yoksa mailto fallback
                const subject = encodeURIComponent(`Randevu Talebi - ${name}`);
                const body = encodeURIComponent(`Ad: ${name}\nE-posta: ${email}\nTelefon: ${phone}\n\nMesaj:\n${message}`);
                window.location.href = `mailto:${settings.notificationEmail || settings.email}?subject=${subject}&body=${body}`;
                setFormStatus('sent');
                formRef.current.reset();
              }
              setTimeout(() => setFormStatus('idle'), 5000);
            }}>
              {/* Honeypot - bot tuzağı, gizli alan */}
              <input type="text" name="_hp" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} tabIndex={-1} autoComplete="off" />
              <input type="text" name="name" placeholder="Adınız" required maxLength={100} />
              <input type="email" name="email" placeholder="E-posta" required maxLength={200} />
              <input type="tel" name="phone" placeholder="Telefon (opsiyonel)" maxLength={20} />
              <textarea rows={4} name="message" placeholder="Mesajınız / Dövme fikriniz..." required maxLength={2000} />
              <button
                type="submit"
                className="btn-primary"
                style={{ width: '100%', marginTop: 8 }}
                disabled={formStatus === 'sending'}
              >
                {formStatus === 'sending' ? 'Gönderiliyor...' : formStatus === 'sent' ? '✓ Gönderildi!' : formStatus === 'error' ? 'Hata — Tekrar Deneyin' : 'Gönder'}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerLogo}>
            <span>X</span> HAUS
          </div>
          <div className={styles.footerCopy}>{settings.footerCopy || '© 2024 X HAUS TATTO ART STUDIO — ESKİŞEHİR'}</div>
          <div className={styles.footerCopy}>{settings.footerSlogan || 'CRAFTED WITH ART'}</div>
        </div>
      </section>
    </main>
  );
}
